import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useCallback, useState } from 'react';
import { exportPdf, exportXlsx, type ExportTable } from '../lib/exportTable.js';

/**
 * ปุ่มดาวน์โหลด PDF / Excel — ใช้ชุดเดียวกันทุกหน้าจอ
 *
 * ⚠ รับ `build` เป็นฟังก์ชัน ไม่ใช่รับตารางสำเร็จรูป
 *
 * ตารางบางหน้ามีเป็นพันแถวและต้องประกอบจากผลกรองที่ผู้ใช้ตั้งไว้
 * ถ้ารับเป็นค่า จะต้องประกอบใหม่ทุกครั้งที่หน้าจอ re-render ทั้งที่ผู้ใช้ยังไม่ได้กดอะไร
 * รับเป็นฟังก์ชันแล้วประกอบตอนกด จึงจ่ายค่าใช้จ่ายเฉพาะตอนที่มีคนใช้จริง
 *
 * ⚠ ต้องมีสถานะ "กำลังสร้าง" ให้เห็น
 * ทั้งสองรูปแบบต้องโหลดไลบรารีก้อนใหญ่ก่อน (pdfmake ~400 KB, exceljs ~900 KB)
 * ครั้งแรกที่กดจึงหน่วงหลายวินาทีบนเน็ตช้า ถ้าปุ่มไม่เปลี่ยนอะไรเลย ผู้ใช้จะกดซ้ำ
 * แล้วได้ไฟล์ซ้ำหลายใบในโฟลเดอร์ดาวน์โหลด
 */
export function ExportButtons({
  build,
  pdfOverride,
  compact = false,
}: {
  /**
   * ⚠ คืน Promise ได้ — บางหน้าจอโหลดรายการทีละหน้า
   *
   * แผงบัญชีอัตราโหลดครั้งละ 200 แถว ถ้าประกอบตารางจากแถวที่โหลดมาแล้วเท่านั้น
   * ผู้ใช้ที่ยังไม่ได้กด "แสดงเพิ่ม" จะได้ไฟล์ 200 แถวจากยอด 770 โดยที่ไฟล์นั้น
   * ไม่มีอะไรบอกเลยว่ามันไม่ครบ — แล้วมันจะถูกแนบเรื่องไปทั้งอย่างนั้น
   * หน้าจอแบบนั้นจึงต้องไล่ดึงทุกหน้าตอนกด ซึ่งเป็นงาน async
   */
  build: () => ExportTable | Promise<ExportTable>;
  /**
   * ตัวสร้าง PDF ของหน้านั้นเอง — ใช้เมื่อสิ่งที่ต้องออกเป็น "เอกสาร" ไม่ใช่ "ตาราง"
   *
   * ⚠ มีผลกับปุ่ม PDF เท่านั้น Excel ยังออกเป็นตารางเสมอ และต้องเป็นอย่างนั้น
   *   คนที่กด Excel ต้องการข้อมูลไปกรองและคำนวณต่อ ไม่ได้ต้องการหนังสือราชการ
   */
  pdfOverride?: () => Promise<void>;
  /** true = แสดงไอคอนอย่างเดียว ใช้ในหัวหน้าต่างที่พื้นที่แคบ */
  compact?: boolean;
}) {
  const [busy, setBusy] = useState<'pdf' | 'xlsx' | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const run = useCallback(
    async (kind: 'pdf' | 'xlsx') => {
      if (busy) return;
      setBusy(kind);
      setFailed(null);
      try {
        if (kind === 'pdf' && pdfOverride) {
          await pdfOverride();
        } else {
          const data = await build();
          if (kind === 'pdf') await exportPdf(data);
          else await exportXlsx(data);
        }
      } catch (err) {
        /*
         * โหลดไลบรารีไม่สำเร็จ (เน็ตหลุดกลางคัน) หรือสร้างไฟล์ไม่ผ่าน
         * ต้องบอกผู้ใช้ ไม่ใช่เงียบ — ปุ่มที่กดแล้วไม่มีอะไรเกิดขึ้นเลย
         * ผู้ใช้จะกดซ้ำอีกหลายครั้งแล้วสรุปว่าระบบพัง
         */
        setFailed(err instanceof Error ? err.message : 'สร้างไฟล์ไม่สำเร็จ');
      } finally {
        setBusy(null);
      }
    },
    [build, busy, pdfOverride],
  );

  const PDF_TIP = 'ดาวน์โหลด PDF — เอกสารสำหรับอ่านและแนบเรื่อง';
  const XLS_TIP = 'ดาวน์โหลด Excel — ข้อมูลสำหรับกรอง เรียง และคำนวณต่อ';

  /*
   * ────────────────────────────────────────────────────────────────
   *  โหมด compact — ยืมคลาส .icobtn ของแถบเครื่องมือมาใช้ทั้งดุ้น
   * ────────────────────────────────────────────────────────────────
   *
   * ⚠ ห้ามเขียนขนาดปุ่มซ้ำที่นี่ ต้องใช้คลาสเดียวกับปุ่มข้าง ๆ เท่านั้น
   *
   * เดิมทำเป็นปุ่มของตัวเองแล้วตั้ง padding ให้ "พอ ๆ กัน" ผลคือปุ่ม PDF/Excel
   * เล็กกว่าปุ่มอื่นในแถบเดียวกันอย่างเห็นได้ชัด เพราะ .icobtn เป็นวงกลม 38×38
   * ส่วนของเราเป็นเม็ดยาที่ความสูงมาจาก padding + ความสูงบรรทัด
   *
   * ที่สำคัญกว่าคือ ต่อให้ตั้งเป็น 38px ให้ตรงกันวันนี้ วันที่มีใครแก้ .icobtn
   * เป็น 36px ปุ่มสองใบนี้จะค้างที่ 38px แล้วแถบก็จะเบี้ยวอีกโดยไม่มีอะไรฟ้อง
   * (บั๊กเดียวกับที่เคยเกิดกับ .ocv-burger มาแล้ว)
   *
   * ไม่ครอบ <div> ด้วย เพราะ .flow-tools เป็น flex ที่มี gap ของตัวเอง
   * ครอบเมื่อไรปุ่มสองใบนี้จะกลายเป็นก้อนเดียวที่มีระยะห่างภายในคนละค่ากับเพื่อน
   */
  if (compact) {
    return (
      <>
        <button
          type="button"
          className="icobtn"
          onClick={() => void run('pdf')}
          disabled={busy !== null}
          aria-label="ดาวน์โหลดเป็นไฟล์ PDF"
          /* ⚠ ใช้ data-tip ไม่ใช่ title — .icobtn วาดคำอธิบายเองด้วย ::after
             ใส่ title ด้วยจะได้คำอธิบายซ้อนกันสองอัน */
          data-tip={failed ?? (busy === 'pdf' ? 'กำลังสร้างไฟล์…' : 'ดาวน์โหลด PDF')}
        >
          {busy === 'pdf' ? <DownloadOutlined spin /> : <FilePdfOutlined />}
        </button>
        <button
          type="button"
          className="icobtn"
          onClick={() => void run('xlsx')}
          disabled={busy !== null}
          aria-label="ดาวน์โหลดเป็นไฟล์ Excel"
          data-tip={failed ?? (busy === 'xlsx' ? 'กำลังสร้างไฟล์…' : 'ดาวน์โหลด Excel')}
        >
          {busy === 'xlsx' ? <DownloadOutlined spin /> : <FileExcelOutlined />}
        </button>
      </>
    );
  }

  return (
    <div className="xbtns">
      <button
        type="button"
        onClick={() => void run('pdf')}
        disabled={busy !== null}
        aria-label="ดาวน์โหลดเป็นไฟล์ PDF"
        title={PDF_TIP}
      >
        {busy === 'pdf' ? <DownloadOutlined spin /> : <FilePdfOutlined />}
        <span>PDF</span>
      </button>
      <button
        type="button"
        onClick={() => void run('xlsx')}
        disabled={busy !== null}
        aria-label="ดาวน์โหลดเป็นไฟล์ Excel"
        title={XLS_TIP}
      >
        {busy === 'xlsx' ? <DownloadOutlined spin /> : <FileExcelOutlined />}
        <span>Excel</span>
      </button>
      {/* role="status" ให้โปรแกรมอ่านหน้าจอประกาศเองโดยไม่ต้องย้ายโฟกัส */}
      {failed && (
        <span className="xfail" role="status">
          {failed}
        </span>
      )}
    </div>
  );
}
